import { useState } from 'react';
import { ProyectoMock } from '../../../../mocks/proyectos';
import { RespuestaAutor, InformeAvance, RetroalimentacionComite } from './types';
import { calcPorcentajeAvance, calcPuntajePromedio, getDecisionColor, getPuntajeBadge, getRondaLabel, EtapaAvance } from './helpers';

interface Props {
  proyectos: ProyectoMock[];
  proyectosConDecision: ProyectoMock[];
  respuestasEnviadas: RespuestaAutor[];
  respuestasPorCodigo: Record<string, RespuestaAutor[]>;
  informesAvance: InformeAvance[];
  retroalimentaciones: RetroalimentacionComite[];
  retroPorCodigo: Record<string, RetroalimentacionComite[]>;
}

export default function TabReportes({ proyectos, proyectosConDecision, respuestasEnviadas, respuestasPorCodigo, informesAvance, retroalimentaciones, retroPorCodigo }: Props) {
  const [proyectoExpandido, setProyectoExpandido] = useState<string | null>(null);
  const [mostrarMetodologia, setMostrarMetodologia] = useState(false);

  const getPorcentajeReporte = (p: ProyectoMock): { pct: number; detalle: string[]; etapas: EtapaAvance[] } => {
    const informe = informesAvance.filter(i => i.codigo === p.codigo).slice(-1)[0];
    if (informe) {
      const calc = calcPorcentajeAvance(p, respuestasEnviadas, informesAvance, retroalimentaciones);
      return { pct: calc.porcentaje, detalle: calc.detalle, etapas: calc.etapas };
    }
    const calc = calcPorcentajeAvance(p, respuestasEnviadas, informesAvance, retroalimentaciones);
    return { pct: calc.porcentaje, detalle: calc.detalle, etapas: calc.etapas };
  };

  const etapasMetodologia = [
    { num: 1, nombre: 'Recepción y asignación', peso: 10, desc: 'Proyecto recibido, código asignado, investigador registrado', color: 'slate' },
    { num: 2, nombre: 'Evaluación inicial', peso: 20, desc: 'Evaluadores asignados, evaluación completada, puntaje calculado', color: 'teal' },
    { num: 3, nombre: 'Decisión oficial — 1ª ronda', peso: 20, desc: 'Decisión del comité emitida, carta enviada al investigador', color: 'teal' },
    { num: 4, nombre: 'Corrección del investigador', peso: 15, desc: 'Documento corregido recibido (respuesta a 1ª ronda)', color: 'amber' },
    { num: 5, nombre: '2ª ronda de revisión', peso: 15, desc: 'Retroalimentación del comité emitida (2ª evaluación)', color: 'amber' },
    { num: 6, nombre: 'Corrección final / 3ª ronda', peso: 10, desc: 'Documento corregido recibido (respuesta a 2ª ronda)', color: 'amber' },
    { num: 7, nombre: 'Aprobación definitiva', peso: 10, desc: 'Trabajo aprobado sin correcciones — proceso culminado', color: 'emerald' },
  ];

  const colorClasses: Record<string, { bg: string; border: string; text: string; bar: string; badge: string }> = {
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', bar: 'bg-slate-400', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', bar: 'bg-teal-500', badge: 'bg-teal-100 text-teal-600 border-teal-200' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: 'bg-amber-400', badge: 'bg-amber-100 text-amber-600 border-amber-200' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-600 border-emerald-200' },
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center bg-teal-100 rounded text-teal-700"><i className="ri-list-check" /></span>
          Reporte general — Evolución y avance de todos los proyectos
        </h3>
        <p className="text-xs text-slate-400 mt-0.5 ml-7">
          Visión objetiva del estado, avance en porcentaje (calculado por etapas del proceso de revisión) y respuestas de los autores.
        </p>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          METODOLOGÍA DE CÁLCULO DE AVANCE
      ═══════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setMostrarMetodologia(!mostrarMetodologia)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
              <i className="ri-book-open-line text-teal-700" />
            </span>
            <div className="text-left">
              <p className="text-sm font-bold text-slate-800">Metodología de cálculo de avance</p>
              <p className="text-xs text-slate-500">Cómo se calcula el porcentaje de avance por etapas del proceso de revisión por pares</p>
            </div>
          </div>
          <span className="w-6 h-6 flex items-center justify-center text-slate-400">
            <i className={mostrarMetodologia ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </span>
        </button>

        {mostrarMetodologia && (
          <div className="px-5 pb-5 pt-2 border-t border-slate-100">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-teal-800 leading-relaxed">
                El <strong>porcentaje de avance</strong> refleja el progreso real del proyecto a través de las etapas del proceso de revisión por pares.
                Cada etapa tiene un peso asignado según su importancia en el flujo de trabajo. El sistema detecta automáticamente qué etapas están completadas
                basándose en los datos registrados en el sistema (evaluaciones, decisiones, documentos recibidos, retroalimentaciones).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {etapasMetodologia.map(etapa => {
                const cls = colorClasses[etapa.color];
                return (
                  <div key={etapa.num} className={`border rounded-lg p-3 ${cls.bg} ${cls.border}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold ${cls.badge}`}>
                        {etapa.num}
                      </span>
                      <span className={`text-xs font-bold ${cls.text}`}>{etapa.nombre}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed mb-2">{etapa.desc}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white/60 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${cls.bar}`} style={{ width: `${etapa.peso}%` }} />
                      </div>
                      <span className={`text-xs font-bold ${cls.text}`}>{etapa.peso}%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-amber-600 flex-shrink-0 mt-0.5">
                <i className="ri-information-line" />
              </span>
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong>Nota importante:</strong> Las etapas 4-6 son iterativas. Si el comité aprueba el trabajo en la 2ª ronda de revisión,
                se omite la etapa 6 y se suma directamente el 10% de aprobación definitiva. El máximo permitido son 3 rondas de revisión.
                Un proyecto aprobado en 1ª ronda alcanza el 100% de avance inmediatamente.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Resumen estadístico */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total proyectos', value: proyectos.length, icon: 'ri-folder-line', cls: 'bg-slate-50 border-slate-200 text-slate-700', iconCls: 'bg-slate-100 text-slate-600' },
          { label: 'Con decisión', value: proyectosConDecision.length, icon: 'ri-award-line', cls: 'bg-teal-50 border-teal-200 text-teal-700', iconCls: 'bg-teal-100 text-teal-600' },
          { label: 'Informes de avance', value: informesAvance.length, icon: 'ri-file-chart-line', cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', iconCls: 'bg-emerald-100 text-emerald-600' },
          { label: 'Retroalimentaciones', value: retroalimentaciones.length, icon: 'ri-feedback-line', cls: 'bg-amber-50 border-amber-200 text-amber-700', iconCls: 'bg-amber-100 text-amber-600' },
        ].map(stat => (
          <div key={stat.label} className={`border rounded-lg p-4 ${stat.cls}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-7 h-7 flex items-center justify-center rounded-md ${stat.iconCls}`}>
                <i className={`${stat.icon} text-sm`} />
              </span>
              <span className="text-xs font-medium opacity-80">{stat.label}</span>
            </div>
            <p className="text-2xl font-bold">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Tabla de proyectos con desglose de etapas */}
      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-bar-chart-2-line" /></span>
          Estado y avance por proyecto
          <span className="text-[10px] font-normal text-slate-400 ml-1">— porcentaje calculado por etapas del proceso de revisión</span>
        </h4>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Código</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Título / Investigador</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Evaluación</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Decisión</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Informes</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Retros</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Avance %</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {proyectos.map(p => {
                const puntaje = calcPuntajePromedio(p);
                const puntajeBadge = getPuntajeBadge(puntaje);
                const informesP = informesAvance.filter(i => i.codigo === p.codigo).length;
                const retrosP = (retroPorCodigo[p.codigo] || []);
                const { pct: avancePct, etapas } = getPorcentajeReporte(p);
                const decCfg = p.decision && p.decision !== '—' ? getDecisionColor(p.decision) : null;
                const expandido = proyectoExpandido === p.codigo;

                return (
                  <>
                    <tr key={p.codigo} className="bg-white hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setProyectoExpandido(expandido ? null : p.codigo)}>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-teal-700 whitespace-nowrap">{p.codigo}</td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-semibold text-slate-800 max-w-[200px] truncate">{p.titulo}</p>
                        <p className="text-[10px] text-slate-400">{p.investigador}</p>
                      </td>
                      <td className="px-4 py-3">
                        {puntajeBadge ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold flex items-center gap-1 w-fit ${puntajeBadge.cls}`}>
                            <span className="w-3 h-3 flex items-center justify-center"><i className={puntajeBadge.icon} /></span>
                            {puntajeBadge.label}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Sin evaluar</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {decCfg ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex items-center gap-1 w-fit ${decCfg.bg}`}>
                            <span className={`w-3 h-3 flex items-center justify-center ${decCfg.iconColor}`}><i className={decCfg.icon} /></span>
                            {p.decision.length > 20 ? p.decision.substring(0, 20) + '...' : p.decision}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Pendiente</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {informesP > 0 ? (
                          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            {informesP} enviado{informesP > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">Sin informe</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {[2, 3].map(r => {
                            const retro = retrosP.find(x => x.ronda === r);
                            return (
                              <div key={r} className={`w-5 h-5 flex items-center justify-center rounded-full text-[9px] font-bold border ${
                                retro ? (retro.requiereNuevaCorreccion ? 'bg-amber-400 border-amber-400 text-white' : 'bg-emerald-500 border-emerald-500 text-white') : 'bg-white border-slate-200 text-slate-400'
                              }`}>
                                {retro ? <i className={retro.requiereNuevaCorreccion ? 'ri-edit-line' : 'ri-check-line'} /> : r}
                              </div>
                            );
                          })}
                          <span className="text-[10px] text-slate-400 ml-1">{retrosP.length}/2</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-slate-100 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${avancePct >= 80 ? 'bg-emerald-500' : avancePct >= 50 ? 'bg-teal-500' : 'bg-amber-400'}`} style={{ width: `${avancePct}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-slate-600">{avancePct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="w-6 h-6 flex items-center justify-center text-slate-400">
                          <i className={expandido ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                        </span>
                      </td>
                    </tr>
                    {expandido && (
                      <tr>
                        <td colSpan={8} className="px-4 py-4 bg-slate-50 border-t border-slate-100">
                          <div className="space-y-3">
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pie-chart-line" /></span>
                              Desglose de avance por etapas
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {etapas.map((etapa, idx) => {
                                const cls = colorClasses[etapa.color] || colorClasses.slate;
                                return (
                                  <div key={idx} className={`flex items-center gap-2 p-2 rounded-md border ${etapa.completada ? cls.bg + ' ' + cls.border : 'bg-white border-slate-200'}`}>
                                    <span className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${etapa.completada ? cls.badge : 'bg-slate-100 text-slate-400 border border-slate-200'}`}>
                                      {etapa.completada ? <i className="ri-check-line text-[10px]" /> : <span className="text-[9px] font-bold">{idx + 1}</span>}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-[11px] font-semibold truncate ${etapa.completada ? cls.text : 'text-slate-400'}`}>{etapa.nombre}</p>
                                      <p className="text-[10px] text-slate-500 truncate">{etapa.descripcion}</p>
                                    </div>
                                    <span className={`text-[10px] font-bold flex-shrink-0 ${etapa.completada ? cls.text : 'text-slate-400'}`}>{etapa.peso}%</span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                              <span className="text-xs text-slate-500">Avance total:</span>
                              <div className="flex-1 bg-slate-200 rounded-full h-2 max-w-48">
                                <div className={`h-2 rounded-full ${avancePct >= 80 ? 'bg-emerald-500' : avancePct >= 50 ? 'bg-teal-500' : 'bg-amber-400'}`} style={{ width: `${avancePct}%` }} />
                              </div>
                              <span className={`text-sm font-bold ${avancePct >= 80 ? 'text-emerald-600' : avancePct >= 50 ? 'text-teal-600' : 'text-amber-500'}`}>{avancePct}%</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Respuestas de autores */}
      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-reply-line" /></span>
          Respuestas de autores recibidas — todas las rondas
        </h4>
        {respuestasEnviadas.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-400">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2"><i className="ri-inbox-line text-3xl" /></div>
            <p className="text-sm font-medium">Sin respuestas de autores aún</p>
            <p className="text-xs mt-1">Cuando los autores respondan las decisiones del comité, aparecerán aquí.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {respuestasEnviadas.map(resp => (
              <div key={resp.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="flex items-start justify-between gap-4 px-5 py-4 bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-bold text-teal-700 bg-teal-50 border border-teal-100 px-2.5 py-0.5 rounded-md">{resp.codigo}</span>
                    <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${resp.dentroDelPlazo ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {getRondaLabel(resp.ronda)}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">{resp.titulo || resp.codigo}</p>
                      <p className="text-[10px] text-slate-400">Autor: {resp.investigador} · {resp.fechaEnvio} · {resp.dentroDelPlazo ? 'En plazo' : 'Fuera de plazo'}</p>
                    </div>
                  </div>
                  <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full font-medium flex items-center gap-1 flex-shrink-0">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-checkbox-circle-fill" /></span>
                    Recibida
                  </span>
                </div>
                <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {resp.actividadesRealizadas && (
                    <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Actividades realizadas</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{resp.actividadesRealizadas}</p>
                    </div>
                  )}
                  {resp.resultadosObtenidos && (
                    <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Resultados obtenidos</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{resp.resultadosObtenidos}</p>
                    </div>
                  )}
                  {resp.dificultades && (
                    <div className="bg-amber-50 rounded-md p-3 border border-amber-100">
                      <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide mb-1">Dificultades</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{resp.dificultades}</p>
                    </div>
                  )}
                  {resp.observacionesAdicionales && (
                    <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Observaciones adicionales</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{resp.observacionesAdicionales}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Retroalimentaciones del comité */}
      {retroalimentaciones.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-feedback-line" /></span>
            Retroalimentaciones del Comité emitidas
          </h4>
          <div className="space-y-2">
            {retroalimentaciones.map(retro => (
              <div key={retro.id} className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-4">
                <span className="font-mono text-xs font-bold text-teal-700 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-md flex-shrink-0">{retro.codigo}</span>
                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border flex-shrink-0 ${
                  retro.ronda === 2 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'
                }`}>
                  {retro.ronda === 2 ? '2ª Retro' : '3ª Retro'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700 truncate">{retro.observaciones}</p>
                  <p className="text-[10px] text-slate-400">{retro.fechaRetro}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                  retro.requiereNuevaCorreccion
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}>
                  {retro.requiereNuevaCorreccion ? 'Requiere corrección' : 'Aprobado'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Informes de avance */}
      {informesAvance.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-chart-line" /></span>
            Informes de avance enviados
          </h4>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Código</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Investigador</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Avance calculado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Fecha</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {informesAvance.map(inf => (
                  <tr key={inf.id} className="bg-white hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-teal-700">{inf.codigo}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{inf.investigador}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5 max-w-16">
                          <div className={`h-1.5 rounded-full ${inf.porcentaje >= 80 ? 'bg-emerald-500' : inf.porcentaje >= 50 ? 'bg-teal-500' : 'bg-amber-400'}`} style={{ width: `${inf.porcentaje}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600">{inf.porcentaje}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{inf.fechaInforme}</td>
                    <td className="px-4 py-3">
                      <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">Enviado</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}